/**
 * API contracts — plan §2.4. These zod schemas are the single definition of
 * every request and response shape; server and client both import them, so a
 * contract cannot drift between the two.
 */
import { z } from 'zod';
import { PERMISSION_AREAS, PERMISSION_LEVELS, DEFAULT_APPS } from './permissions.js';
import { ERROR_CODES } from './errors.js';
import { ORG_UNIT_KINDS } from './enums.js';
import { MFA_KINDS } from './mfa.js';
import { myNotificationPrefsSchema } from './my-notifications.js';
export const uuid = z.string().uuid();
/** Every record carries these; mutations echo `version` back for concurrency. */
export const recordMeta = z.object({
    id: uuid,
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    version: z.number().int().nonnegative(),
});
export const apiErrorBody = z.object({
    code: z.enum(ERROR_CODES),
    message: z.string(),
    field: z.string().optional(),
    details: z.record(z.string(), z.unknown()).optional(),
});
/** Plan §2.4: cursor pagination on every list endpoint. */
export const listQuery = z.object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    scope: z.string().optional(),
    view: z.string().optional(),
    q: z.string().optional(),
    sort: z.string().optional(),
    group: z.string().optional(),
    fields: z.string().optional(),
});
export function listResponse(item) {
    return z.object({
        items: z.array(item),
        nextCursor: z.string().nullable(),
        total: z.number().int().nonnegative().optional(),
    });
}
/** Mutations send the version they read; a mismatch is a 409. */
export const versioned = z.object({ version: z.number().int().nonnegative() });
/**
 * Every area is present and every value is a level — `z.record` over an enum
 * infers a Partial, which would let a missing area read as `undefined` at the
 * call sites that gate the UI. Building the shape explicitly keeps the matrix
 * total, the way `emptyMatrix()` guarantees it on the server.
 */
const levelSchema = z.enum([...PERMISSION_LEVELS]);
export const permissionMatrix = z.object(Object.fromEntries(PERMISSION_AREAS.map((area) => [area, levelSchema])));
/** Partial form, for a PATCH that changes only some areas. */
export const permissionMatrixPatch = permissionMatrix.partial();
// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------
export const signInRequest = z.object({
    email: z.string().email(),
    password: z.string().min(1),
    rememberDevice: z.boolean().default(false),
    /**
     * Which door, when the address has two.
     *
     * Only ever sent by the chooser, and only for the side whose password did
     * *not* open at the first attempt: picking that option asks for its own
     * password and sends it back named. Absent means "whichever this password
     * opens", which is every ordinary sign-in.
     *
     * It is a narrowing, never a shortcut — the server still checks both
     * passwords exactly as it would have, and `side` only decides which match
     * it is willing to honour. A request naming a side with the other side's
     * password is refused like any other wrong password.
     */
    side: z.enum(['tenant', 'platform']).optional(),
});
export const signInResponse = z.discriminatedUnion('result', [
    z.object({ result: z.literal('signed_in') }),
    z.object({ result: z.literal('mfa_required'), methods: z.array(z.enum(MFA_KINDS)) }),
]);
/**
 * Answering a challenge — `docs/identity-and-sign-in.md` §4.4.
 *
 * `method` is the kind, and `methodId` names *which* of that kind when
 * somebody holds several (two authenticators, three passkeys). A `code` for
 * the three kinds that ask for digits, a `response` for the one that does not:
 * a passkey's answer is a WebAuthn assertion, which is an object, not a string
 * somebody types.
 *
 * `recovery` is a kind here but not in `MFA_KINDS`, because it is not a method
 * anybody sets up or sees on a list — it is the way back in when every method
 * has failed, and it has always been accepted at this endpoint.
 */
export const mfaVerifyRequest = z.object({
    method: z.union([z.enum(MFA_KINDS), z.literal('recovery')]),
    methodId: z.string().uuid().optional(),
    code: z.string().min(4).max(64).optional(),
    response: z.unknown().optional(),
    /**
     * The same box, asked again after the code.
     *
     * It has to be here as well as on `sign-in`: an account with two-step on
     * never reaches `finishTenantSignIn`, so the session that actually opens is
     * the one this route makes, and until now it was always the short one. An
     * admin account, where two-step is never optional, could therefore never be
     * remembered at all.
     *
     * A caller can lie about it, and that is not a hole worth closing: they have
     * just produced the password *and* the second factor, and all it decides is
     * how long their own session lasts. The step-up window is ten minutes either
     * way, so nothing privileged is bought by it.
     */
    rememberDevice: z.boolean().default(false),
});
/** Picking a door when the address has two — `/auth/choose`. */
export const chooseRequest = z.object({
    /* An auth ticket — a two-minute single-use credential (§2.6), not a visit.
       The 0102 rename left this word alone on purpose. */
    ticket: z.string().min(8).max(200),
    rememberDevice: z.boolean().default(false),
});
/** Asking for a fresh six digits, by email or by text (§4.2). */
export const mfaSendRequest = z.object({
    challenge: z.string().min(8),
    method: z.enum(['email', 'sms']),
});
export const magicLinkRequest = z.object({ email: z.string().email() });
export const consumeTokenRequest = z.object({ token: z.string().min(32) });
/**
 * The shortest password any door accepts — eight characters (Aaron, step 27;
 * it was twelve). One number, so the set-password page, the invite pages, both
 * change-password forms and every schema that checks one say the same thing.
 */
export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_TOO_SHORT = `Use at least ${PASSWORD_MIN_LENGTH} characters.`;
export const setPasswordRequest = z.object({
    token: z.string().min(32),
    password: z.string().min(PASSWORD_MIN_LENGTH, PASSWORD_TOO_SHORT),
});
export const enrollTotpResponse = z.object({
    secret: z.string(),
    otpauthUrl: z.string(),
});
export const confirmTotpRequest = z.object({ code: z.string().length(6) });
export const recoveryCodesResponse = z.object({ codes: z.array(z.string()) });
export const setPinRequest = z.object({ pin: z.string().regex(/^\d{4,6}$/, 'A PIN is 4 to 6 digits.') });
export const pinSignInRequest = z.object({
    userId: uuid,
    pin: z.string().regex(/^\d{4,6}$/),
});
export const stepUpRequest = z.object({ password: z.string().min(1) });
// ---------------------------------------------------------------------------
// Session / me
// ---------------------------------------------------------------------------
/**
 * One place, as the scope switcher draws it — `docs/scope-switcher.md` §3.2.
 *
 * The counts are of what this person *reaches*, not of what exists: a
 * district manager's district says "2 branches" because two is their whole
 * world there, and a count of the company's nine would be a number they can
 * do nothing with.
 */
export const orgUnitSummary = z.object({
    id: uuid,
    kind: z.enum(ORG_UNIT_KINDS),
    name: z.string(),
    code: z.string().default(''),
    parentId: uuid.nullable(),
    branchCount: z.number().int().nonnegative(),
    districtCount: z.number().int().nonnegative().default(0),
    /** A branch's default yard and crews — the meta line under its name. */
    yardName: z.string().nullable().default(null),
    crewCount: z.number().int().nonnegative().default(0),
    closed: z.boolean().default(false),
    reachable: z.boolean(),
});
export const meResponse = z.object({
    /*
     * The session's CSRF token, so a reload can keep making mutations.
     *
     * Without it the token was only ever handed out by sign-in, which meant a
     * refresh, a bookmark or a link into the app left the client unable to send
     * any POST at all — including the list reads, which are POSTs because a view
     * is a body rather than a query string. It is not a secret from the person
     * holding the session: it is bound to their cookie and worthless without it.
     */
    csrfToken: z.string(),
    user: z.object({
        id: uuid,
        name: z.string(),
        email: z.string().email(),
        initials: z.string(),
        /**
         * The two halves of the name, and the whole.
         *
         * `users.name` is derived from the parts by a trigger (migration 0066), so
         * My account edits the parts: a form that wrote the whole string would be
         * writing the derived value, and the next save would overwrite it from
         * underneath.
         */
        firstName: z.string(),
        lastName: z.string(),
        /** What it says under the name on a quote. Not a role; it gates nothing. */
        title: z.string(),
        phone: z.string(),
        mfaEnrolled: z.boolean(),
        hasPin: z.boolean(),
        passwordChangedAt: z.string().datetime().nullable(),
        /**
         * What their roles still ask of them (§6.10's invite → password → MFA →
         * PIN). The setup flow needs to know before it can decide whether there
         * is a step 3 or a step 4, and guessing from the role's name would be a
         * second definition of a rule the role already carries.
         */
        mfaRequired: z.boolean(),
        pinExpected: z.boolean(),
        /**
         * One-shot codes still unspent.
         *
         * The one number worth saying beside "two-step is on": somebody who has
         * used eight of ten needs to know before the ninth, and a screen that
         * never counted them can only find out by locking them out.
         */
        recoveryCodesLeft: z.number().int().default(0),
        /** Where their day starts and ends — from their branch up the org tree. */
        timeZone: z.string().default('UTC'),
        /** What reaches them, and where (`my-notifications.ts`). */
        notificationPrefs: myNotificationPrefsSchema
            .default({ matrix: {}, quietHours: null }),
    }),
    /**
     * A throwaway copy of the world (§6.13). The frame draws a banner, and
     * every provider is stubbed behind it.
     */
    demo: z.object({ idleMinutes: z.number().int() }).nullable().default(null),
    tenant: z.object({
        id: uuid,
        /** `company_profile.display_name` — what a customer sees (0075). */
        name: z.string(),
        logoUrl: z.string().nullable(),
        logoDarkUrl: z.string().nullable(),
        brandColor: z.string(),
        currency: z.string(),
    }),
    /**
     * Every role this person holds and where (0075).
     *
     * The UI needs them for the same reason the server does: a button that
     * edits a job belonging to Lancaster should not be drawn for somebody whose
     * only grant is at Harrisburg. `permissions` above stays the union, so a
     * screen that only asks "may they do this at all" is unchanged.
     */
    grants: z.array(z.object({
        id: uuid,
        orgUnitId: uuid,
        orgUnitName: z.string(),
        kind: z.enum(ORG_UNIT_KINDS),
        roleId: uuid,
        roleName: z.string(),
        permissions: permissionMatrix,
    })),
    roles: z.array(z.object({ id: uuid, name: z.string() })),
    defaultApp: z.enum(DEFAULT_APPS),
    permissions: permissionMatrix,
    /**
     * Where this person is looking — `docs/scope-switcher.md` §2.2.
     *
     * `available` is their whole organization, including the units above their
     * grants, which are drawn greyed so the tree still reads top-down. `roots`
     * is what they ticked; `selected` is the branches that expands to, which is
     * what every list is actually narrowed by. `label` is the only sentence:
     * the header, the user menu, the AI panel and the search footer all print
     * it rather than composing one of their own.
     */
    scope: z.object({
        available: z.array(orgUnitSummary),
        roots: z.array(uuid).default([]),
        selected: z.array(uuid),
        label: z.string(),
        labelKind: z.enum(ORG_UNIT_KINDS).default('company'),
    }),
    /**
     * Somebody from Rowted, in this account, right now (§6.12).
     *
     * Everything the banner needs to say who and why — the customer is entitled
     * to both. `id` so the banner's Stop button has something to end.
     */
    impersonation: z
        .object({
        id: uuid,
        actorName: z.string(),
        reason: z.string(),
        readOnly: z.boolean(),
        expiresAt: z.string(),
    })
        .nullable(),
    /**
     * What this company bought — the keys in `features.ts`, core ones included.
     *
     * Not a permission and not a setting: whether the company *has* the thing
     * at all. It belongs here because it is a fact about the account rather than
     * about a record, and a screen that has to ask a settings endpoint before it
     * knows whether to draw a tab is a screen that flickers. `featuresOf` on the
     * server is the one place the list is worked out, so the client and
     * `requireFeature` cannot disagree about what is on.
     *
     * `.default([])` so a cached `/me` from before this existed still parses —
     * and reads as "nothing sellable", which is the same answer a tenant with
     * no `tenant_features` rows gets.
     */
    features: z.array(z.string()).default([]),
});
export const setScopeRequest = z.object({ orgUnitIds: z.array(uuid).min(1) });
// ---------------------------------------------------------------------------
// Org units, roles, users
// ---------------------------------------------------------------------------
export const orgUnit = recordMeta.extend({
    kind: z.enum(ORG_UNIT_KINDS),
    name: z.string().min(1),
    parentId: uuid.nullable(),
});
export const roleRecord = recordMeta.extend({
    name: z.string().min(1),
    description: z.string(),
    defaultApp: z.enum(DEFAULT_APPS),
    mfaRequired: z.boolean(),
    permissions: permissionMatrix,
});
export const updateRoleRequest = versioned.extend({
    name: z.string().min(1).optional(),
    description: z.string().optional(),
    defaultApp: z.enum(DEFAULT_APPS).optional(),
    mfaRequired: z.boolean().optional(),
    permissions: permissionMatrixPatch.optional(),
});
export const userRecord = recordMeta.extend({
    name: z.string().min(1),
    email: z.string().email(),
    status: z.enum(['invited', 'active', 'suspended']),
    roleIds: z.array(uuid),
    scopeOrgUnitIds: z.array(uuid),
});
export const updateUserRequest = versioned.extend({
    name: z.string().min(1).optional(),
    email: z.string().email().optional(),
    roleIds: z.array(uuid).optional(),
    scopeOrgUnitIds: z.array(uuid).optional(),
});
export const inviteUserRequest = z.object({
    name: z.string().min(1),
    email: z.string().email(),
    roleIds: z.array(uuid).min(1),
    scopeOrgUnitIds: z.array(uuid).min(1),
});
// ---------------------------------------------------------------------------
// Audit / history
// ---------------------------------------------------------------------------
export const auditEntry = z.object({
    id: uuid,
    tableName: z.string(),
    recordId: uuid,
    action: z.string(),
    changedAt: z.string().datetime(),
    userId: uuid.nullable(),
    userName: z.string().nullable(),
    impersonatorUserId: uuid.nullable(),
    impersonatorName: z.string().nullable(),
    source: z.string(),
    reason: z.string().nullable(),
    detail: z.string().nullable(),
    /** `{ field: { old, new } }`, or `{ field: { changed: true } }` when sensitive. */
    changes: z.record(z.string(), z.unknown()),
});
export const historyQuery = listQuery.extend({
    table: z.string().optional(),
    recordId: uuid.optional(),
});
//# sourceMappingURL=api.js.map